import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const SECTION_LABELS = { environmental: '🌱 Environmental', social: '🤝 Social', governance: '⚖️ Governance' };
const LEVEL_META = {
  1: { label: '🥉 Livello 1 — Base',       color: '#97C459' },
  2: { label: '🥈 Livello 2 — Avanzato',   color: '#1D9E75' },
  3: { label: '🥇 Livello 3 — Eccellenza', color: '#085041' },
};

export default function AdminProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'certify' | 'reject'
  const [notes, setNotes] = useState('');
  const [overrideLevel, setOverrideLevel] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    axios.get(`/api/projects/${id}`)
      .then(r => { setProject(r.data); setOverrideLevel(r.data.certificationLevel || ''); })
      .finally(() => setLoading(false));
  }, [id]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      await axios.put(`/api/projects/${id}/certify`, {
        action,
        notes,
        certificationLevel: overrideLevel ? parseInt(overrideLevel) : undefined,
      });
      toast.success(action === 'certify' ? 'Certificato rilasciato!' : 'Progetto rifiutato');
      navigate('/admin/projects');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Errore');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9B9A92' }}>Caricamento...</div>;
  if (!project) return <div style={{ padding: 40 }}>Progetto non trovato</div>;

  const answersBySection = {};
  for (const a of (project.answers || [])) {
    if (!answersBySection[a.section]) answersBySection[a.section] = [];
    answersBySection[a.section].push(a);
  }
  const canAct = ['submitted', 'under_review'].includes(project.status);

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">{project.name}</span>
        <span style={{ fontSize: 13, color: '#9B9A92' }}>{project.company?.name}</span>
      </div>
      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, maxWidth: 1020 }}>

          {/* Main */}
          <div>
            {/* Scores */}
            {project.scoreTotal > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
                <div className="stat-card" style={{ textAlign: 'center' }}>
                  <div className="stat-label">Score ESG</div>
                  <div className="stat-value" style={{ color: '#1D9E75' }}>
                    {project.scorePercentage != null
                      ? `${Math.round(project.scorePercentage)}%`
                      : `${Math.round((project.scoreTotal / 125) * 100)}%`}
                  </div>
                  <div className="stat-sub">{Math.round(project.scoreTotal)}/125</div>
                </div>
                {[
                  { k: 'environmental', label: '🌱 Env', v: project.scoreEnvironmental, max: 30 },
                  { k: 'social', label: '🤝 Social', v: project.scoreSocial, max: 45 },
                  { k: 'governance', label: '⚖️ Gov', v: project.scoreGovernance, max: 50 },
                ].map(s => (
                  <div key={s.k} className="stat-card" style={{ textAlign: 'center' }}>
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value">{Math.round((s.v / s.max) * 100)}%</div>
                    <div className="stat-sub">{Math.round(s.v)}/{s.max}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Answers by section */}
            {['environmental', 'social', 'governance'].map(sectionKey => {
              const sAnswers = answersBySection[sectionKey] || [];
              if (!sAnswers.length) return null;
              return (
                <div key={sectionKey} className="card" style={{ marginBottom: 14 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 12 }}>{SECTION_LABELS[sectionKey]}</h3>
                  {sAnswers.map(a => (
                    <div key={a.questionId} style={{ padding: '10px 0', borderBottom: '1px solid #F0EFE8' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#9B9A92', marginBottom: 3 }}>{a.questionId}</div>
                      <div style={{ fontSize: 13, color: '#1A1A18', marginBottom: 3 }}>{a.answer}</div>
                      <div style={{ fontSize: 11, color: '#1D9E75' }}>+{a.score}/5 punti</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Sidebar action panel */}
          <div>
            {/* Project info */}
            <div className="card" style={{ marginBottom: 14 }}>
              <h4 style={{ fontSize: 13, marginBottom: 10 }}>Dettagli progetto</h4>
              {[
                ['Categoria', project.category],
                ['Deploy', project.deploymentType],
                ['Team', project.teamSize ? `${project.teamSize} pers.` : '—'],
                ['Inviato', project.submittedAt ? new Date(project.submittedAt).toLocaleDateString('it-IT') : '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingBottom: 6, marginBottom: 6, borderBottom: '1px solid #F0EFE8' }}>
                  <span style={{ color: '#9B9A92' }}>{k}</span>
                  <span style={{ fontWeight: 500 }}>{v || '—'}</span>
                </div>
              ))}
            </div>

            {/* Level suggested */}
            {project.certificationLevel && (
              <div className="card" style={{ marginBottom: 14, textAlign: 'center', borderColor: LEVEL_META[project.certificationLevel]?.color }}>
                <div style={{ fontSize: 12, color: '#9B9A92', marginBottom: 4 }}>Livello calcolato automaticamente</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: LEVEL_META[project.certificationLevel]?.color }}>
                  {LEVEL_META[project.certificationLevel]?.label}
                </div>
              </div>
            )}

            {/* Action form */}
            {canAct && (
              <div className="card">
                <h4 style={{ fontSize: 13, marginBottom: 12 }}>Emetti decisione</h4>
                {!action ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={() => setAction('certify')}>
                      ✅ Certifica il progetto
                    </button>
                    <button className="btn btn-danger" style={{ justifyContent: 'center' }} onClick={() => setAction('reject')}>
                      ❌ Rifiuta il progetto
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 500, color: action === 'certify' ? '#1D9E75' : '#A32D2D', marginBottom: 12 }}>
                      {action === 'certify' ? '✅ Certificazione' : '❌ Rifiuto'}
                    </div>
                    {action === 'certify' && (
                      <div className="form-group">
                        <label style={{ fontSize: 12 }}>Livello certificazione</label>
                        <select className="form-control" value={overrideLevel} onChange={e => setOverrideLevel(e.target.value)}>
                          <option value="1">Livello 1 — Base</option>
                          <option value="2">Livello 2 — Avanzato</option>
                          <option value="3">Livello 3 — Eccellenza</option>
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label style={{ fontSize: 12 }}>Note per il cliente {action === 'reject' ? '(obbligatorio)' : '(opzionale)'}</label>
                      <textarea className="form-control" rows={3} placeholder="Motivazioni, suggerimenti..." value={notes} onChange={e => setNotes(e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setAction(null)}>Annulla</button>
                      <button
                        className={`btn btn-sm ${action === 'certify' ? 'btn-primary' : 'btn-danger'}`}
                        style={{ flex: 1, justifyContent: 'center' }}
                        disabled={processing || (action === 'reject' && !notes)}
                        onClick={handleAction}
                      >
                        {processing ? '...' : 'Conferma'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {!canAct && project.status === 'certified' && (
              <div className="card" style={{ textAlign: 'center', background: '#EAF3DE', borderColor: '#97C459' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>🏅</div>
                <div style={{ fontWeight: 500, color: '#3B6D11' }}>Certificato emesso</div>
                <div style={{ fontSize: 12, color: '#5C5B55', marginTop: 4 }}>
                  {project.certifiedAt && new Date(project.certifiedAt).toLocaleDateString('it-IT')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
